// LWI-1502 — pure helpers behind the MSR sticky summary bar.
// Status codes match the backend (Draft 1, Pick and Pack 2, Bill of Lading 3,
// In Transit 4, Delivered 5); kept here because the API returns only the code.
import { isDomesticUs as isDomesticUsLane } from "@utils/country";

export const CUSTOMS_PROCESSING_LABEL = "Customs Processing";
export const CARRIER_STATUS = 3;

/**
 * LWI-1508 — Bill of Lading onward means the MSR is at the carrier and belongs
 * to the TMS: no cancel, no revise, no pulling back to Pick and Pack.
 */
export const isAtCarrier = (status) => Number(status) >= CARRIER_STATUS;

/**
 * LWI-1508 — "Customs Processing" is derived, not stored (GDLS decision B):
 * an international MSR at Pick and Pack whose customs invoice does not exist
 * yet is waiting on customs. Both facts must be KNOWN — isDomesticUs exactly
 * false (the same test that gates the customs invoice) and hasCustomsInvoice
 * exactly false. Unknown never claims the state, so an API that does not
 * send the flag simply shows Pick and Pack.
 */
export const isCustomsProcessing = ({ status, isDraft, cancelledAt, isDomesticUs, hasCustomsInvoice } = {}) =>
  Number(status) === 2 &&
  !isDraft &&
  !cancelledAt &&
  isDomesticUs === false &&
  hasCustomsInvoice === false;

export const MSR_STATUS_LABELS = {
  1: "Draft",
  2: "Pick and Pack",
  3: "Bill of Lading",
  4: "In Transit",
  5: "Delivered",
};

export const AWAITING_PICKUP_LABEL = "Awaiting Pickup";

/**
 * LWI-1732 — stored status 3 whose shipment the TMS already has (load /
 * shipment number or import stamp) is "Awaiting Pickup", the same stage the
 * wizard stepper shows. Callers derive `tmsAccepted` from msrLifecycle's
 * tmsHasShipment (details) or the msr-list row's `tmsAccepted` (dashboard).
 * Every other stored status keeps its MSR_STATUS_LABELS word.
 */
export const storedStatusLabel = (status, tmsAccepted = false) => {
  const code = Number(status);
  if (code === 3 && tmsAccepted) return AWAITING_PICKUP_LABEL;
  return MSR_STATUS_LABELS[code] || (status ? `Status ${status}` : "—");
};

/**
 * Status column of the MSR Dashboard grid (and its export). Cancelled, Draft and
 * Customs Processing win; stored 3 + row.tmsAccepted (LWI-1732) is Awaiting
 * Pickup, the server's Status filter/sort label too.
 */
export const dashboardStatusLabel = (value, row) => {
  // LWI-1342 - cancelled regardless of the numeric pipeline.
  if (row?.cancelledAt) return "Cancelled";
  // LWI-1360 - a draft shows "Draft" until submitted.
  if (row?.isDraft) return "Draft";
  // LWI-1508 - international at Pick and Pack with no customs invoice yet.
  if (
    isCustomsProcessing({
      status: value,
      isDraft: row?.isDraft,
      cancelledAt: row?.cancelledAt,
      isDomesticUs: isDomesticUsLane(row?.shipFrom?.country, row?.shipTo?.country),
      hasCustomsInvoice: row?.hasCustomsInvoice,
    })
  )
    return CUSTOMS_PROCESSING_LABEL;
  // LWI-1420 - residual status 1 (pre-migration) belongs to Pick and Pack.
  if (Number(value) === 1) return MSR_STATUS_LABELS[2];
  if (Number(value) >= 1 && Number(value) <= 5) return storedStatusLabel(value, !!row?.tmsAccepted);
  return value ?? "";
};

/**
 * Workflow status + submission state for the bar.
 * Cancelled wins over everything; a draft is "not submitted" whatever its code;
 * anything at Pick and Pack or later has been submitted.
 */
export const summarizeMsr = ({ status, isDraft, cancelledAt, isDomesticUs, hasCustomsInvoice, tmsAccepted = false } = {}) => {
  const code = Number(status);
  const statusLabel = storedStatusLabel(status, tmsAccepted);

  if (cancelledAt) {
    return {
      statusLabel,
      statusColor: "error",
      submission: { label: "Cancelled", color: "error" },
    };
  }
  if (isDraft || code === 1) {
    return {
      statusLabel,
      statusColor: "default",
      submission: { label: "Draft — not submitted", color: "warning" },
    };
  }
  if (isCustomsProcessing({ status, isDraft, cancelledAt, isDomesticUs, hasCustomsInvoice })) {
    return {
      statusLabel: CUSTOMS_PROCESSING_LABEL,
      statusColor: "warning",
      submission: { label: "Submitted", color: "success" },
    };
  }
  if (code >= 2) {
    return {
      statusLabel,
      statusColor: "primary",
      submission: { label: "Submitted", color: "success" },
    };
  }
  return {
    statusLabel,
    statusColor: "default",
    submission: { label: "Not submitted", color: "default" },
  };
};

/** Same rule as the backend ("resolved" = status contains "Resolve"). */
export const isOpenException = (row) =>
  !/resolve/i.test(String(row?.exception_status ?? row?.status ?? ""));

export const describeOpenExceptions = (rows) => {
  if (rows === null || rows === undefined) {
    return { count: null, label: "Checking exceptions…", color: "default" };
  }
  const count = (Array.isArray(rows) ? rows : []).filter(isOpenException).length;
  if (count === 0) {
    return { count, label: "No open exceptions", color: "success" };
  }
  return {
    count,
    label: `${count} open exception${count === 1 ? "" : "s"}`,
    color: "error",
  };
};
